import {
  getMessaging,
  isSupported,
  onMessage,
  onRegistered,
  onUnregistered,
  register as registerMessaging,
  unregister as unregisterMessaging,
  type Messaging
} from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { app, functions } from '@/modules/firebase';

declare const __JIDOKA_VAPID_KEY__: string;

const registerDeviceCallable = httpsCallable<Record<string, unknown>, unknown>(functions, 'registerNotificationDevice');
const unregisterDeviceCallable = httpsCallable<{ fid: string }, unknown>(functions, 'unregisterNotificationDevice');
const FID_STORAGE_KEY = 'JIDOKA_NOTIFICATION_FID';

let messagingInstance: Messaging | null = null;
let listenersInstalled = false;

async function getSupportedMessaging(): Promise<Messaging | null> {
  if (!('serviceWorker' in navigator) || !('Notification' in window) || !await isSupported()) return null;
  messagingInstance ||= getMessaging(app);
  return messagingInstance;
}

async function installListeners(messaging: Messaging): Promise<void> {
  if (listenersInstalled) return;
  listenersInstalled = true;
  onRegistered(messaging, fid => {
    localStorage.setItem(FID_STORAGE_KEY, fid);
    void registerDeviceCallable({
      fid,
      label: navigator.platform || 'Browser',
      platform: navigator.userAgent
    });
  });
  onUnregistered(messaging, fid => {
    localStorage.removeItem(FID_STORAGE_KEY);
    void unregisterDeviceCallable({ fid });
  });
  onMessage(messaging, payload => {
    window.dispatchEvent(new CustomEvent('jidoka:notification-received', { detail: payload.data || {} }));
  });
}

async function registerCurrentBrowser(messaging: Messaging): Promise<void> {
  const serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
  await installListeners(messaging);
  await registerMessaging(messaging, {
    serviceWorkerRegistration,
    ...(__JIDOKA_VAPID_KEY__ ? { vapidKey: __JIDOKA_VAPID_KEY__ } : {})
  });
}

export async function initializeNotificationPush(): Promise<void> {
  if (Notification.permission !== 'granted') return;
  const messaging = await getSupportedMessaging();
  if (messaging) await registerCurrentBrowser(messaging);
}

export async function enableNotificationPush(): Promise<'enabled' | 'denied' | 'unsupported'> {
  const messaging = await getSupportedMessaging();
  if (!messaging) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';
  await registerCurrentBrowser(messaging);
  return 'enabled';
}

export async function disableNotificationPush(): Promise<void> {
  const messaging = await getSupportedMessaging();
  const fid = localStorage.getItem(FID_STORAGE_KEY) || '';
  if (fid) await unregisterDeviceCallable({ fid });
  localStorage.removeItem(FID_STORAGE_KEY);
  if (messaging) await unregisterMessaging(messaging);
}

export function getPushPermissionState(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

export function isNotificationPushEnabled(): boolean {
  return getPushPermissionState() === 'granted' && Boolean(localStorage.getItem(FID_STORAGE_KEY));
}
